import React from "react";
import { motion } from "framer-motion";
import { FiCheckCircle } from "react-icons/fi";

/**
 * Reusable ACE-style "standards we support" block for Training category pages.
 */
const TrainingStandardsSection = ({
  heading,
  intro,
  benefits = [],
  standards = [],
  groups = [],
}) => {
  return (
    <section
      className="bg-white py-12 md:py-16 px-4 sm:px-6 lg:px-8 border-t border-gray-100"
      aria-label={heading}
    >
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="max-w-3xl mb-10"
        >
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
            {heading}
          </h2>
          <div
            className="w-16 h-1 bg-[#F86624] rounded-full mb-5"
            aria-hidden="true"
          />
          {intro && (
            <p className="text-gray-600 text-base md:text-lg leading-relaxed">
              {intro}
            </p>
          )}
        </motion.div>

        {standards.length > 0 && (
          <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4 list-none mb-10">
            {standards.map((item, index) => (
              <motion.li
                key={item}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.35, delay: index * 0.03 }}
                className="flex items-start gap-2.5 bg-gray-50 border border-gray-100 rounded-xl px-4 py-3.5 text-sm text-gray-800"
              >
                <FiCheckCircle
                  size={16}
                  className="text-[var(--bg-primary)] mt-0.5 flex-shrink-0"
                  aria-hidden="true"
                />
                <span>{item}</span>
              </motion.li>
            ))}
          </ul>
        )}

        {groups.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">
            {groups.map((group, index) => (
              <motion.div
                key={group.title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.06 }}
                className="bg-gray-50 border border-gray-100 rounded-2xl p-6"
              >
                <h3 className="text-base font-bold text-[var(--bg-primary)] mb-4">
                  {group.title}
                </h3>
                <ul className="space-y-2.5 list-none">
                  {group.items.map((item) => (
                    <li
                      key={item}
                      className="flex items-start gap-2 text-sm text-gray-700"
                    >
                      <FiCheckCircle
                        size={15}
                        className="text-[#F86624] mt-0.5 flex-shrink-0"
                        aria-hidden="true"
                      />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        )}

        {benefits.length > 0 && (
          <ul className="flex flex-wrap gap-3 list-none">
            {benefits.map((benefit) => (
              <li
                key={benefit}
                className="inline-flex items-center gap-2 text-sm font-medium text-gray-800 bg-[var(--bg-primary)]/5 border border-[var(--bg-primary)]/15 rounded-full px-4 py-2"
              >
                <span className="text-[var(--bg-primary)]" aria-hidden="true">
                  ✔
                </span>
                {benefit}
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
};

export default TrainingStandardsSection;
